/*------------------------------------------------------------------------------------------
* © 2020 KPMG LLP, a Delaware limited liability partnership and 
* a member firm of the KPMG global organization of independent member firms 
* affiliated with KPMG International Limited, a private English company limited by guarantee. 
* All rights reserved.
*-----------------------------------------------------------------------------------------*/
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationType } from 'src/app/models/application-type.model';
import { Slider } from 'src/app/models/slider.model';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppMode, StorageKey, Texts } from 'src/app/models/application-enum.model';
import { EnvSettingsService } from '../core/services/env-settings.service';
import { User } from '../models/user.model';
import * as moment from 'moment';
import { UserActivityLogs, UserActivityTracking } from '../models/user-activity-tracking.model';



@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private _commonApiRoute = '/api/V1/Common';


  public routerUrl: string;
  public applicationTypeInformation: ApplicationType;
  public slider: Slider = <Slider>{};
  public slider$ = new BehaviorSubject<Slider>(this.slider);
  public refreshParent$ = new BehaviorSubject<boolean>(false);
  public pageStartTime: number = 0;
  public initialPageStartTime: number = 0;
  public timeSpentOnPages: UserActivityLogs[] = [];
  constructor(private router: Router, private _envSettings: EnvSettingsService, private _http: HttpClient) {
    this.routerUrl = this.router.url;
  }

  get commonServiceUrl() {
    return this._envSettings.getApiUrl() + this._commonApiRoute;
  }

  getApplicationType() {
    return this._http.get<Array<ApplicationType>>(this.commonServiceUrl + '/GetApplicationTypes');
  }

  searchUsers(searchTerm:string) : Observable<Array<User>> {
    return this._http.get<Array<User>>(this.commonServiceUrl + '/SearchUsers/'+searchTerm);
  }

  getAppDisplayName(appId: string):string {

    const applicationType: ApplicationType[] = JSON.parse(sessionStorage.getItem(StorageKey.ApplicationType));
    if (applicationType && applicationType.length>0 &&(appId === AppMode.evsMode || appId === AppMode.taxMode)) {
     return   applicationType.find(a=>a.applicationTypeId===Number(appId)).applicationDisplayName;
    } else {
      return appId;
    }

  }

  getAppTitle(appId: string): string {
    const applicationType: ApplicationType[] = JSON.parse(sessionStorage.getItem(StorageKey.ApplicationType));

    if (!applicationType) {
      this.getApplicationType().subscribe(async res => {      
        sessionStorage.setItem(StorageKey.ApplicationType, JSON.stringify(res));
      return await res.find(a => a.applicationTypeId === Number(appId)).applicationTitle;
      });
    } else {     
      if (applicationType && applicationType.length > 0 && (appId === AppMode.evsMode || appId === AppMode.taxMode)) {
       return applicationType.find(a => a.applicationTypeId === Number(appId)).applicationTitle;
      }
    }
  }
  /* getAppTitle(appId: string):string {
    let appId = this.getAppId();
    if(appId){
      const applicationType: ApplicationType[] = JSON.parse(this._asyncsessionStorage.getItem(StorageKey.ApplicationType));
      if (applicationType && applicationType.length>0 &&(appId === AppMode.evsMode || appId === AppMode.taxMode)) {
      return   applicationType.find(a=>a.applicationTypeId===Number(appId)).applicationTitle;
      } else {
        return appId;
      }
    }
    return null;
  } */

  getAppId(){
    if (this.router.url.toLowerCase().startsWith('/tax')) {
      return AppMode.taxMode; // TAX MODE
    } else if (this.router.url.toLowerCase().startsWith('/evs')) {
      return AppMode.evsMode; // EVS Mode
    } else {
      return null;
    }
  }

  getAppMode() {
    if (this.router.url.toLowerCase().startsWith('/tax')) {
      return Texts.Tax;
    } else if (this.router.url.toLowerCase().startsWith('/evs')) {
      return Texts.Evs;
    } else {
      return null;
    }
  }
 
  formatNumber(value: any): string {
    let cleanedNumber = value ? value.toString().replace(/,/g, '') : '';
    let parts = cleanedNumber.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts[1]) {
      parts[1] = parts[1].toString().slice(0, 2);
    }
    return parts.join('.');
  }
  formatNumberWithoutDecimal(value: any): string {
    let cleanedNumber = value ? value.toString().replace(/,/g, '') : '';
    let parts = cleanedNumber.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts[1]) {
      parts[1] = parts[1].toString().slice(0, 2);
    }
    return parts[0];
  }
  
  DownloadUserGuide(appId: number): Observable<Blob> {
    return this._http.get(this.commonServiceUrl + '/DownloadUserGuide?appId=' + appId, { responseType: 'blob' });
  }
  copyToClipBoard(conceptName: string) {

    let copyBox = document.createElement('textarea');
    copyBox.style.position = 'fixed';
    copyBox.style.left = '0';
    copyBox.style.top = '0';
    copyBox.style.opacity = '0';
    copyBox.value = conceptName;
    document.body.appendChild(copyBox);
    copyBox.focus();
    copyBox.select();
    document.execCommand('copy');
    document.body.removeChild(copyBox);
  }
  startPageTimer() {
    if (this.initialPageStartTime == 0) {
      this.initialPageStartTime = Date.now();
    }
    this.pageStartTime = Date.now();
  }
  endPageTimer(url:string,user:string) {
    const currentTime = Date.now();
    const duration = currentTime - this.pageStartTime;
    const timeSpent = moment.utc(duration).format('H:mm:ss'); 
    const pageInfo = {
      pageUrl: url,
      pageStartTime: moment.utc(this.pageStartTime).toDate(),
      pageEndTime: moment.utc(currentTime).toDate(),
      timeSpent,
      user
    }  
    this.timeSpentOnPages.push(pageInfo);
    return this.timeSpentOnPages;
  }

  storeUserActivityLogs(userData: UserActivityLogs[], emailAddress:string): Observable<UserActivityTracking>{
    const currentTime = Date.now();
    const duration = currentTime - this.initialPageStartTime;
    const totalTimeSpent = moment.utc(duration).format('H:mm:ss');
    const userInfo: UserActivityTracking = {
      Id:0,
      EmailAddress: btoa(emailAddress),
      ActivityLogs: btoa(JSON.stringify(userData)),
      LocalCreatedDate: moment.utc(Date.now()).toDate(),
      TotalActivityDuration: totalTimeSpent
    }
    return this._http.post<UserActivityTracking>(`${this.commonServiceUrl}/StoreUserActivityLogs`, userInfo);
  }
}
